import { useState, useRef } from 'react'
import { Loader } from '@mantine/core'
import { Select } from '@forwardflow/ui-v2'
import { SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import { FILTER_LABEL_MAP } from '@forwardflow/constants/onboarding.constants'
import { TLeadType } from '@forwardflow/types/campaign.types'
import { useLeadOptions } from '@forwardflow/hooks/customHooks/useLeadOptions'
import { useDebouncedValue } from '@forwardflow/hooks/customHooks/useDebouncedValue'

interface LeadSelectionProps {
   selectedFilterId: TLeadType
   selectedValueId: string | null
   onOptionSelect: (value: string) => void
}

export const LeadSelection = ({
   selectedFilterId,
   selectedValueId,
   onOptionSelect,
}: LeadSelectionProps) => {
   const [searchValue, setSearchValue] = useState('')
   const [debouncedSearch] = useDebouncedValue(searchValue, 500)
   const {
      options: selectData,
      isLoading,
      loadMore,
      hasMore,
   } = useLeadOptions(selectedFilterId, debouncedSearch)
   const dropdownRef = useRef<HTMLElement | null>(null)
   const isLoadingMore = useRef(false)

   const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (hasMore && !isLoading && !isLoadingMore.current) {
         const { scrollHeight, clientHeight, scrollTop } = target
         // Load more when user scrolls within 100px of bottom
         if (scrollTop + clientHeight >= scrollHeight - 100) {
            isLoadingMore.current = true
            loadMore()
            // Reset flag after a delay to prevent rapid calls
            setTimeout(() => {
               isLoadingMore.current = false
            }, 1000)
         }
      }
   }

   return (
      <Select
         searchable
         value={selectedValueId}
         key={selectedFilterId}
         searchValue={searchValue}
         onSearchChange={setSearchValue}
         onChange={(value: string | null, option) => {
            onOptionSelect(
               selectedFilterId === 'assigned_pond_id'
                  ? String(value)
                  : option?.label || ''
            )
         }}
         label={`Select the ${
            FILTER_LABEL_MAP[selectedFilterId as keyof typeof FILTER_LABEL_MAP]
         } you'd like to include.`}
         leftSection={<SearchMagnifyingGlassIcon width={16} />}
         rightSection={isLoading ? <Loader size="xs" /> : undefined}
         placeholder={`Search and select ${
            FILTER_LABEL_MAP[selectedFilterId as keyof typeof FILTER_LABEL_MAP]
         }`}
         data={selectData}
         onDropdownOpen={() => {
            // Attach scroll listener when dropdown opens
            setTimeout(() => {
               const dropdown = document.querySelector(
                  '.mantine-Select-dropdown .mantine-ScrollArea-viewport'
               ) as HTMLElement
               if (dropdown) {
                  dropdown.addEventListener('scroll', handleScroll)
                  dropdownRef.current = dropdown
               }
            }, 0)
         }}
         onDropdownClose={() => {
            // Clean up scroll listener
            if (dropdownRef.current) {
               dropdownRef.current.removeEventListener('scroll', handleScroll)
               dropdownRef.current = null
            }
         }}
      />
   )
}
